import { ArrowLeft, ArrowRight, Discover, X } from '@ui/components/icons';
import { NoSSR } from '@ui/components/NoSSR';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { useBreakpoint } from '@ui/hooks/useBreakPoint';
import { Badge } from '@ui/lib/park/ui/badge';
import { Button } from '@ui/lib/park/ui/button';
import { Icon } from '@ui/lib/park/ui/icon';
import { Tabs } from '@ui/lib/park/ui/tabs';
import { Text } from '@ui/lib/park/ui/text';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';
import { Flex, FlexProps, HStack } from 'styled-system/jsx';

import {
  CategoryTag,
  COMING_SOON_TAGS,
  getCategoryTagLabels,
  getSubCategoryTags,
} from '../../constants';
import { useGetCategories } from '../../hooks/useGetCategories';
import { useDragScroll } from './hooks/useDragScroll';
import { useTabsStore } from './hooks/useMassiveTokenTabsStore';
import { MassiveTokenSearch } from './MassiveTokenSearch';

export const MassiveTokenTabs = ({
  enableSearch = true,
  ...props
}: FlexProps & {
  enableSearch?: boolean;
}) => {
  return (
    <Flex
      position="sticky"
      top={0}
      alignItems="flex-start"
      gap="8px"
      minH="50px"
      flexDir="column"
      {...props}
    >
      <Flex alignItems="center" w="full">
        <DraggableCategories />
      </Flex>
    </Flex>
  );
};

// for desktop , hide the scroll bar but still support horizontal drag and scrolling
const DraggableCategories = () => {
  const { t } = useTranslation();

  const currentCategory = useTabsStore((s) => s.currentCategory);
  const setCategory = useTabsStore((s) => s.setCategory);

  const categories = useGetCategories();

  // Reference for Tabs.List (the scrollable container)
  const tabsListRef = useRef<HTMLDivElement | null>(null);
  const { isAtStart, isAtEnd, scrollTo } = useDragScroll(tabsListRef);

  useEffect(() => {
    if (categories.findIndex((tag) => tag === currentCategory) === -1) {
      setCategory(CategoryTag.ALL, null);
    }
  }, [currentCategory, categories, setCategory]);

  const tradePairs = useTradePairInfos();

  // const longEnoughToScroll = categories.length > 6; // Adjust this value based on your design
  const longEnoughToScroll = false;

  return (
    <NoSSR>
      <Flex alignItems="center" w="full" gap="12px">
        {longEnoughToScroll && (
          <Button
            onClick={() => scrollTo('left')}
            variant="text"
            visibility={isAtStart ? 'hidden' : 'visible'}
          >
            <Icon boxSize="16px">
              <ArrowLeft />
            </Icon>
          </Button>
        )}
        <Tabs.Root
          value={currentCategory}
          defaultValue={CategoryTag.ALL}
          variant="line"
          size="md"
          w="full"
          flex="1"
          minW="0"
          overflow="auto"
          pl="20px"
          lazyMount
          unmountOnExit
        >
          <Tabs.List w="full" boxShadow="none" px="0" ref={tabsListRef}>
            {categories.map((category) => {
              const subCategories = getSubCategoryTags(category, [...tradePairs]);

              return (
                <Tabs.Trigger
                  key={category}
                  value={category}
                  onClick={() => {
                    setCategory(category, subCategories[0]);
                  }}
                  _selected={{ color: 'primary.primary !important' }}
                  _hover={{ color: 'greyWhite.lightGrey2' }}
                  textStyle="text.xlarge"
                  color="greyWhite.lightGrey3"
                  userSelect="none"
                  minW="unset"
                  data-cy={`Category-${category.toUpperCase()}`}
                >
                  {getCategoryTagLabels(t)[category]}
                </Tabs.Trigger>
              );
            })}

            <Tabs.Indicator />
          </Tabs.List>
        </Tabs.Root>
        <HStack
          gap="6px"
          flexShrink={0}
          alignItems="center"
          display={{ base: 'none', desktop: 'flex' }}
        >
          <Text
            color="greyWhite.white"
            fontSize="10px"
            fontWeight="500"
            letterSpacing="0.02em"
            lineHeight="1"
            opacity={0.6}
            whiteSpace="nowrap"
          >
            powered by
          </Text>
          <img
            src="/images/pyth-logo-light.svg"
            alt="Pyth"
            style={{ height: 16, width: 'auto', flexShrink: 0 }}
          />
          <Text
            color="greyWhite.white"
            fontSize="12px"
            fontWeight="700"
            letterSpacing="0.08em"
            lineHeight="1"
          >
            Pro
          </Text>
        </HStack>
        {longEnoughToScroll && (
          <Button
            onClick={() => scrollTo('right')}
            variant="text"
            visibility={isAtEnd ? 'hidden' : 'visible'}
          >
            <Icon boxSize="16px">
              <ArrowRight />
            </Icon>
          </Button>
        )}
      </Flex>
    </NoSSR>
  );
};

const DraggableSubCategories = () => {
  const currentCategory = useTabsStore((s) => s.currentCategory);
  const currentSubCategory = useTabsStore((s) => s.currentSubCategory);
  const setSubCategory = useTabsStore((s) => s.setSubCategory);
  const subCategoriesRef = useRef<HTMLDivElement>(null);
  const { isAtStart, isAtEnd, scrollTo } = useDragScroll(subCategoriesRef, [
    currentCategory,
  ]);
  const tradePairs = useTradePairInfos();

  const subCategories = getSubCategoryTags(currentCategory, [...tradePairs]);
  if (!subCategories?.length) return null;

  return (
    <Flex alignItems="center" gap={2} w="full" position="relative" h={8}>
      <Button
        position="absolute"
        bg="black.lightBlack"
        left={0}
        onClick={() => scrollTo('left')}
        variant="text"
        visibility={isAtStart ? 'hidden' : 'visible'}
      >
        <Icon boxSize="16px">
          <ArrowLeft />
        </Icon>
      </Button>

      <HStack
        ref={subCategoriesRef}
        overflowX="auto"
        css={{
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
          userSelect: 'none',
        }}
        flex={1}
        w="full"
      >
        {subCategories.map((subCategory) => {
          const isSelected = subCategory === currentSubCategory;

          return (
            <Badge
              key={subCategory}
              size="md"
              _hover={{ cursor: 'pointer' }}
              color={isSelected ? 'greyWhite.white' : 'greyWhite.lightGrey3'}
              borderColor={isSelected ? 'primary.orange' : 'transparent'}
              borderWidth="1px"
              onClick={() => {
                if (!isSelected) {
                  setSubCategory(subCategory);
                }
              }}
              bg="black.background"
              data-cy={`SubCategory-${subCategory?.toUpperCase()}`}
            >
              {subCategory}
            </Badge>
          );
        })}

        {/* currently in ui design, only stocks need to show coming soon tags */}
        {currentCategory === CategoryTag.STOCKS &&
          COMING_SOON_TAGS.filter((tag) => !subCategories.includes(tag)).map(
            (subCategory) => {
              return (
                <Badge
                  key={subCategory}
                  size="md"
                  _hover={{ cursor: 'not-allowed' }}
                  color="greyWhite.lightGrey3"
                  borderColor="transparent"
                  borderWidth="1px"
                  bg="black.lv2"
                  gap={1}
                  data-cy={`SubCategory-${subCategory?.toUpperCase()}`}
                >
                  {subCategory}
                  <Text textStyle="text.xxsmall" color="primary.orangeLight">
                    Soon!
                  </Text>
                </Badge>
              );
            },
          )}
      </HStack>

      <Button
        position="absolute"
        bg="black.lightBlack"
        right={0}
        onClick={() => scrollTo('right')}
        variant="text"
        visibility={isAtEnd ? 'hidden' : 'visible'}
      >
        <Icon boxSize="16px">
          <ArrowRight />
        </Icon>
      </Button>
    </Flex>
  );
};
